import { FunctionalComponent } from 'preact'
import { GetServerSideProps } from '../../server-middleware/context'
import { z } from 'zod'
import WidgetContainer, { widgetContainerConfig } from '../widget-container'

export const widgetName = 'banner' as const

const positionConfig = z.enum(['Top', 'Bottom', 'Left', 'Right'])
const textAlignmentConfig = z.enum(['Left', 'Center', 'Right'])

export const config = z.object({
  content: z.object({
    textAlignment: textAlignmentConfig,
    title: z.string(),
    description: z.string()
  }),
  buttons: z.object({
    primaryText: z.string(),
    primaryUrl: z.string(),
    secondaryText: z.string().optional(),
    secondaryUrl: z.string().optional()
  }).optional(),
  image: z.object({
    src: z.string(),
    position: positionConfig
  }).optional()
}).merge(widgetContainerConfig)

export type Position = z.infer<typeof positionConfig>
export type TextAlignment = z.infer<typeof textAlignmentConfig>

export type BannerElementConfig = z.infer<typeof config>

export const getServerSideProps: GetServerSideProps<BannerElementConfig, BannerElementConfig> = async (_, config) => {
  return config
}

function flexDirection (position: Position): string {
  switch (position) {
    case 'Top':
      return 'column'
    case 'Bottom':
      return 'column-reverse'
    case 'Left':
      return 'row'
    case 'Right':
      return 'row-reverse'
  }
}

const Banner: FunctionalComponent<BannerElementConfig> = ({ content, buttons, image, backgroundColor, backgroundColorDisplay }: BannerElementConfig) => {
  const isPrimaryButton = Boolean(buttons?.primaryUrl) && Boolean(buttons?.primaryText)
  const isSecondaryButton = Boolean(buttons?.secondaryUrl) && Boolean(buttons?.primaryText)
  const textAlignment = content.textAlignment.toLowerCase()
  return (
    <WidgetContainer
      alwaysAddPadding
      backgroundColor={backgroundColor}
      backgroundColorDisplay={backgroundColorDisplay}
      widgetName='banner'
    >
      <div className='qa-banner-container banner-container'>
        <div className='sitewidth banner-widget__layout' style={{ flexDirection: flexDirection(image?.position || 'Top') }}>
          {Boolean(image?.src) &&
            <div className='banner-widget__image' style={{ paddingBottom: image?.position === 'Top' ? '24px' : 0 }}>
              <div className='col'>
                <img src={image?.src || ''} />
              </div>
            </div>}
          <div className='banner-widget__contents' style={{ paddingBottom: image?.position === 'Bottom' ? '24px' : 0 }}>
            <div className={`col text-${textAlignment}`}>
              {Boolean(content?.title) && <h2 className='widget__heading'>{content.title}</h2>}
              {Boolean(content?.description) &&
                <div dangerouslySetInnerHTML={{ __html: content.description }} />}
            </div>
            {(isPrimaryButton || isSecondaryButton) &&
              <div className={`col text-${textAlignment} banner-widget__button`}>
                {isPrimaryButton && <a href={buttons?.primaryUrl} className='btn--cta'>{buttons?.primaryText}</a>}
                {isSecondaryButton && <a href={buttons?.secondaryUrl} className='btn--secondary'>{buttons?.secondaryText}</a>}
              </div>}
          </div>
        </div>
      </div>
    </WidgetContainer>
  )
}

export default Banner
